import { GetTimeseriesRequest } from "../types/timeseries";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  deleteAreaOfInterestService,
  getAreaOfInterestDetailService,
  getAreaOfInterestsService,
  postAreaOfInterestPreviewService,
  postAreaOfInterestService,
} from "../services/areaOfInterest";
import { useAuthContext } from "./useAuthContext";
import useQueryState from "./useQueryState";
import { useToast } from "../Components/Provider/ToastProvider";

export type UseTimeseries = GetTimeseriesRequest;
type AreaOfInterestAttributes = {
  country: string;
  file: File[];
  areaOfInterestName: string;
  idAttribute: string;
  nameAttribute: string;
};

const useAreaOfInterest = ({
  id,
}: {
  id?: string;
} = {}) => {
  const { showToast } = useToast();
  const { parsedToken } = useAuthContext();
  const {
    queryState: { country },
  } = useQueryState();
  const { email: userEmail } = parsedToken || {};
  const queryClient = useQueryClient();

  const getAreaOfInterests = useQuery(
    ["areaOfInterest", userEmail, country],
    async () => {
      return await getAreaOfInterestsService({ country });
    },
    {
      refetchInterval: (data) => {
        if (!data?.data) return false;

        const hasProcessing = data.data.some(
          (item: any) => item.status !== "finished"
        );

        return hasProcessing ? 30_000 : false;
      },
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: !!userEmail && !!country,
    }
  );

  const getAreaOfInterestsDetail = useQuery(
    ["areaOfInterest", id, userEmail],
    async () => {
      return await getAreaOfInterestDetailService({ id: id ?? "" });
    },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: false,
      enabled: !!id && !!userEmail,
    }
  );

  const postAreaOfInterest = useMutation(
    async (attributes: AreaOfInterestAttributes) => {
      return await postAreaOfInterestService(attributes);
    },
    {
      retry: false,
      onSuccess: () => {
        queryClient.refetchQueries(["areaOfInterest", userEmail, country]);
      },
    }
  );

  const postAreaOfInterestPreview = useMutation(
    async (payload: { files: File[] }) => {
      return await postAreaOfInterestPreviewService(payload);
    },
    {
      retry: false,
    }
  );

  const deleteAreaOfInterest = useMutation(
    async ({ id }: { id: string }) => {
      return deleteAreaOfInterestService({ id });
    },
    {
      retry: false,
      onSuccess: () => {
        showToast("success", "Area of interest deleted");
        queryClient.refetchQueries(["areaOfInterest", userEmail, country]);
      },
    }
  );

  return {
    getAreaOfInterests,
    getAreaOfInterestsDetail,
    postAreaOfInterest,
    postAreaOfInterestPreview,
    deleteAreaOfInterest,
  };
};

export default useAreaOfInterest;
